/** External */
import _ from "lodash";
import React from "react";
import { Button } from "@toddle-edu/ds-web";
import { connect } from "react-redux";
import { createPortal } from "react-dom";
import { SquareFilled } from "@toddle-edu/ds-icons";
import { graphql, compose } from "lib/apolloClient";

/** Internal */
import DotLoadingIcon from "UIComponents/DotLoadingIcon";
import { useI18n } from "Hooks";
import { WORKSHEET_ELEMENT_IDS } from "Worksheet/modules/WorksheetConstants";
import { getAIConversationsQuery } from "Worksheet/modules/WorksheetQueries";
import { getAIConversationsFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import { updateAIConversation, uploadFileV2 } from "modules/Services";
import {
  updateAssessment,
  updateAssessmentInCache,
} from "Assessments/modules/AssessmentModule";
import {
  updateActiveQuestion,
  updateQuestionToCache,
  updateWorksheetSidebar,
  updateAIAssessmentTools,
  createWorksheetQuestionAI,
  updateGeneratingAIQuestions,
  createWorksheetQuestionAIV2,
  updateAddQuestionsInBetween,
} from "Worksheet/modules/WorksheetModules";

/** Relative */
import classes from "./AiQuestionLoader.scss";
import { useAiQuestionStream } from "./hooks/useAiQuestionStream";
import { useAiQuestionPlanMaker } from "./hooks/useAiQuestionPlanMaker";
import { useAiQuestionGenerator } from "./hooks/useAiQuestionGenerator";
import { useSaveAiUsageMetricsId } from "./hooks/useSaveAiUsageMetricsId";
import { updateQuestionToCacheV2 } from "./modules/AiQuestionLoaderModule";

// * Constants
const PORTAL_ID = WORKSHEET_ELEMENT_IDS.WORKSHEET_QUESTION_CONTENT_AREA_ID;

/**
 *
 * @param {*} props
 * @returns Stop Generating Question Button - Shows a button to stop the AI question generation
 */
const StopGeneratingQuestionButton = ({ onClick, disabled = false }) => {
  const { t } = useI18n();

  const portalContent = (
    <div className={classes.stopGenerationButtonPortal}>
      <Button
        dsVersion="2.0"
        size="large"
        variant="neutral"
        onClick={onClick}
        icon={<SquareFilled />}
        className={
          disabled ? classes.stopGenerateBtnDisabled : classes.stopGenerateBtn
        }
      >
        {t("common:stop_with_label", {
          label: t("classRoom:generating"),
        })}
      </Button>
    </div>
  );

  const portalElement = document && document.getElementById(PORTAL_ID);
  if (!portalElement) return null;
  return createPortal(portalContent, portalElement);
};

/**
 *
 * @param {*} props
 * @returns AI Question Loader Step One - Shows when the AI is creating a plan for question generation
 */
const AiQuestionLoaderStepOne = props => {
  const { loadingTitle, stopGeneration } = useAiQuestionPlanMaker(props);

  return (
    <div className={classes.questionLoader}>
      <div className={classes.loadingContainer}>
        <DotLoadingIcon />
        <div className="text-subtitle-2">{loadingTitle}</div>
      </div>
      <StopGeneratingQuestionButton onClick={stopGeneration} />
    </div>
  );
};

/**
 *
 * @param {*} props
 * @returns AI Question Loader Step Two - Shows when the AI is generating questions
 */
const AiQuestionLoaderStepTwo = props => {
  const { t } = useI18n();
  useAiQuestionStream(props);
  useSaveAiUsageMetricsId(props);
  const { stopGeneration, stopTriggered } = useAiQuestionGenerator(props);

  return (
    <div className={classes.questionLoader}>
      <div className={classes.loadingContainer}>
        <DotLoadingIcon />
        <div className="text-subtitle-2">
          {t("worksheet:working_out_questions")}
        </div>
      </div>

      <StopGeneratingQuestionButton
        disabled={stopTriggered}
        onClick={stopGeneration}
      />
    </div>
  );
};

/**
 *
 * @param {*} props
 * @returns AI Question Loader
 */
const AiQuestionLoader = props => {
  const { step } = props;
  return step === 1 ? (
    <AiQuestionLoaderStepOne {...props} />
  ) : (
    <AiQuestionLoaderStepTwo {...props} />
  );
};

const mapStateToProps = state => {
  const portalType = _.get(state, "login.userInfo.portalType");
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const activeQuestionId = _.get(state, "worksheet.activeQuestion");

  const isAddingQuestionsInBetween = _.get(
    state,
    "worksheet.addQuestionsInBetween.isAddingQuestionsInBetween",
    false
  );

  const questionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionResourceId",
    null
  );

  const parentResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentResourceId",
    null
  );

  const questionId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionId",
    null
  );

  const parentItemId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentItemId",
    null
  );

  return {
    portalType,
    questionId,
    parentItemId,
    activeQuestionId,
    parentResourceId,
    questionResourceId,
    isGeneratingAIQuestions,
    isAddingQuestionsInBetween,
  };
};

const mapActionCreators = {
  uploadFileV2,
  updateAssessment,
  updateAIConversation,
  updateActiveQuestion,
  updateQuestionToCache,
  updateWorksheetSidebar,
  updateAIAssessmentTools,
  updateAssessmentInCache,
  updateQuestionToCacheV2,
  createWorksheetQuestionAI,
  createWorksheetQuestionAIV2,
  updateGeneratingAIQuestions,
  updateAddQuestionsInBetween,
};

AiQuestionLoader.displayName = "AiQuestionLoader";

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getAIConversations, ownProps: { isData, isLoading } }) {
      const queryData = getAIConversationsFromCache(
        getAIConversations.variables
      );
      const networkStatus = _.get(getAIConversations, "networkStatus");
      const messages = [..._.get(queryData, "messages.edges", [])].reverse();
      const pageInfo = _.get(queryData, "messages.pageInfo", {});
      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const conversationMetadata = _.get(queryData, "metadata", {});

      return {
        messages,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        conversations: queryData,
        isInitialState: _.isEmpty(messages),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        conversationMetadata,
        getAiConversationRefetch: getAIConversations.refetch,
      };
    },
  })
)(AiQuestionLoader);
